import type { ID } from "@/constants";
import type { IOption } from "@/interfaces";
import { EmptyOption } from "@/interfaces";

/**
 * get the file size in bytes in convert to KB, MB, GB
 * @param size file size
 */
export function humanizedFileSize(size: number) {
  if (size === 0) return "0 Bytes";
  const kilo = 1000;
  const dm = 2;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
  const i = Math.floor(Math.log(size) / Math.log(kilo));

  return `${Number.parseFloat((size / kilo ** i).toFixed(dm))} ${sizes[i]}`;
}

/**
 * convert null or undefined to empty string ('')
 * @param value value
 */
export function nullToString(value: null | string | undefined) {
  if (value === null || value === undefined) return "";
  return value;
}
/**
 * convert null to empty dropdown
 * @param value value
 */
export function nullToOption(value: null | IOption<ID>) {
  if (value === null) return EmptyOption;
  return value;
}

export function excludeNull<T>(value: null | undefined | T): T | undefined {
  if (value === null) return undefined;
  return value;
}

const englishChars = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "."];

export function p2e(text: string): string {
  return text.replaceAll(/[٫۰-۹]/g, (char) => {
    return englishChars["۰۱۲۳۴۵۶۷۸۹٫".indexOf(char)];
  });
}

export function removeLeadingZeros(value: string) {
  const regex = new RegExp("^0+(?!$)", "g");

  return value.replaceAll(regex, "");
}

/**
 * Checks if a given string represents a valid number.
 *
 * @param {string} str - The string to be checked.
 * @returns {boolean} Returns `true` if the string represents a valid number, `false` otherwise.
 *
 * @example
 * ```typescript
 * isNumber('123'); // true
 * isNumber('123.45'); // true
 * isNumber('123E+1'); // true
 * isNumber('abc'); // false
 * ```
 */
export function isNumber(str: string): boolean {
  return (
    // Allows empty string
    str === "" ||
    /^[+-]$/.test(str) ||
    // Matches formats like '123', '123.45', '123E+1'
    /^[+-]?\d+(\.\d*)?(?:[Ee][+-]?\d+)?$/.test(str) ||
    // Matches formats like '123E', '123E+', '123E-'
    /^[+-]?\d+(\.\d*)?(?:[Ee][+-]?)?$/.test(str)
  );
}

export function isDigit(str: string): boolean {
  return str === "" || /^\d*$/.test(str);
}
